import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { PageHeader } from './Card';
import { Banner } from './Banner';

/**
 * True when a lazy page (or its CSS) failed to download. After a deploy, the hashed file an
 * open tab asks for no longer exists. Each browser and bundler words it differently.
 */
export function isChunkLoadError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: unknown; message?: unknown };
  if (e.name === 'ChunkLoadError') return true;
  const msg = typeof e.message === 'string' ? e.message : '';
  return /dynamically imported module|Importing a module script failed|Loading (CSS )?chunk [\w-]+ failed|Unable to preload CSS|error loading dynamically imported module/i.test(
    msg,
  );
}

type State = { error: Error | null };

class Boundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept in the console for debugging; the screen shows a plain message.
    console.error('Screen error', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const reload = (
      <button type="button" className="btn primary" onClick={() => window.location.reload()}>
        Reload
      </button>
    );
    if (isChunkLoadError(error)) {
      return (
        <>
          <PageHeader title="A new version of SEO HQ is available" />
          <Banner tone="info" actions={reload}>
            A new version of SEO HQ is available. Reload to continue.
          </Banner>
        </>
      );
    }
    return (
      <>
        <PageHeader title="This screen hit an error" sub="The rest of SEO HQ still works. Pick another screen in the sidebar, or reload this one." />
        <Banner tone="bad" actions={reload}>
          <span className="mono">{error.message || 'Unknown error'}</span>
        </Banner>
      </>
    );
  }
}

/**
 * Catches render errors and failed lazy imports for the current screen, inside `<main>`, so the
 * sidebar and top bar stay usable. Resets when the route changes (keyed by pathname).
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <Boundary key={pathname}>{children}</Boundary>;
}
